import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import swal from "sweetalert2";
import MatchPlayList from "../matchPlayList";
import { emailMatchPlayWinner, getMatchPlayScorecard, getMatchPlayWinner, postScoreCard, verifyScore, walkoverChangeAPI } from "../../api/matchPlayAPI";
import ScoreModal from "./scoreModal";
import MatchCard from "./matchCard";
import ScorecardModal from "./scorecardModal";

import DEFAULT_USER_PHOTO from '../../../assets/images/matchPlay_DefaultUserIcon.png';
import VS_IMAGE from '../../../assets/images/matchPlay_VS.png';
// ─── Helpers ──────────────────────────────────────────────────────────────────
const getRoundName = (index, total) => {
    if (index === total - 3) return total > 5 ? "Quarters" : "Quarter Final";
    if (index === total - 2) return total > 5 ? "Semis" : "Semi Final";
    if (index === total - 1) return "Final";
    return `Round ${index + 1}`;
};

// ─── MatchPlayScorecard (Main) ────────────────────────────────────────────────
const MatchPlayScorecard = (props) => {

    //#region State
    const [mMaster, setMMaster]                           = useState(null);
    const [matchPlay, setMatchPlay]                       = useState({});
    const [rounds, setRounds]                             = useState([]);
    const [selectedRound, setSelectedRound]               = useState({ round: 1, roundName: "Round 1" });
    const [registeredPlayers, setRegisteredPlayers]       = useState([]);
    const [matchPlayPlayers, setMatchPlayPlayers]         = useState([]);
    const [matchPlayPlayersVs, setMatchPlayPlayersVs]     = useState([]);
    const [matchPlayWinners, setMatchPlayWinners]         = useState([]);
    const [matchPlayWinnerHoles, setMatchPlayWinnerHoles] = useState([]);
    const [isFinalWinnerDeclared, setIsFinalWinnerDeclared] = useState(false);

    // Drawer — always mounted, toggled via `drawerOpen`
    const [drawerOpen, setDrawerOpen]       = useState(false);
    const [activePlayerVs, setActivePlayerVs] = useState(null);
    const [activeWinner, setActiveWinner]   = useState(null);

    // Modals
    const [showScoreModal, setShowScoreModal]   = useState(false);

    const userPhotoURL = window?.Config?.Core?.UserPhotoURL || "";
    //#endregion

    useEffect(() => {
        props.title?.("Match Play Scorecard", "matchplay-scorecard");
    }, []);

    //#region Helpers
    const getActiveWinnerHoles = useCallback(() => {
        if (!activeWinner) return [];
        return matchPlayWinnerHoles.filter(h => h.MatchPlayPlayerVsID === activeWinner.MatchPlayPlayerVsID);
    }, [activeWinner, matchPlayWinnerHoles]);

    const getWinnersForVs = useCallback((vsId) =>
        matchPlayWinners.find(w => w.MatchPlayPlayerVsID === vsId),
    [matchPlayWinners]);

    const getWinnerHolesForVs = useCallback((vsId) =>
        matchPlayWinnerHoles.filter(h => h.MatchPlayPlayerVsID === vsId),
    [matchPlayWinnerHoles]);

    const currentRoundVs = useCallback(() => {
        const round = rounds.find(r => r.Round === selectedRound.round);
        if (!round) return [];
        return matchPlayPlayersVs
            .filter(vs => vs.MatchPlayRoundID === round.ID)
    }, [rounds, selectedRound, matchPlayPlayersVs]);
    //#endregion

    //#region API
    const applyResponse = (res) => {
        if (!res) return;
        const updatedRounds = (res.MatchPlayRound || []).map((round, index, arr) => ({
            ...round,
            roundName: getRoundName(index, arr.length),
        }));
        setMatchPlay(res.MatchPlay || {});
        setRounds(updatedRounds);
        setRegisteredPlayers(res.MatchPlayRegister || []);
        setMatchPlayPlayers(res.MatchPlayPlayers || []);
        setMatchPlayPlayersVs(res.MatchPlayPlayersVs || []);
        setMatchPlayWinners(res.MatchPlayerWinners || []);
        setMatchPlayWinnerHoles(res.MatchPlayWinnerHole || []);
        setIsFinalWinnerDeclared(res.IsFinalWinnerDeclared || false);
        setSelectedRound({
            round:     updatedRounds[0]?.Round     ?? 1,
            roundName: updatedRounds[0]?.roundName ?? "Round 1",
        });
        if (res.action === "x") {
            swal.fire({
                icon: "warning", buttonsStyling: false, showConfirmButton: false,
                titleText: "Oops..",
                text: "Match play draw not created, kindly create draw before entering score.",
                timer: 2500,
            });
        }
    };

    const fetchMatchPlayScorecard = async (matchPlayId) => {
        try {
            swal.fire({ titleText: "Loading", text: "Please Wait...", showConfirmButton: false, allowOutsideClick: false, onOpen: () => swal.showLoading() });
            const res = await getMatchPlayScorecard(matchPlayId);
            swal.close();
            applyResponse(res);
        } catch (err) {
            swal.close();
            swal.fire({ icon: "error", titleText: err.message || "Failed to load data" });
        }
    };

    const refreshWinners = async (matchPlayId) => {
        try {
            swal.fire({ titleText: "Loading", text: "Please Wait...", showConfirmButton: false, allowOutsideClick: false, onOpen: () => swal.showLoading() });
            // const res = await getMatchPlayScorecard(matchPlayId); // reuse same GET or swap for a dedicated endpoint
            const res = await getMatchPlayWinner(matchPlayId); 
            swal.close();
            setMatchPlayPlayers(res.MatchPlayPlayers || []);
            setMatchPlayPlayersVs(res.MatchPlayPlayersVs || []);
            setMatchPlayWinners(res.MatchPlayerWinners || []);
            setMatchPlayWinnerHoles(res.MatchPlayWinnerHole || []);
            // Close drawer after refresh
            setDrawerOpen(false);
        } catch (err) {
            swal.close();
            swal.fire({ icon: "error", titleText: err.message });
        }
    };

    const postScore = async ({ leaderId, thru, score }) => {
        try {
            const isPlayOff = thru > 18;
            const winnerPayload = matchPlay?.Type === "Singles"
                ? { ID: 0, MatchPlayPlayerVsID: activePlayerVs.ID, User1ID: leaderId, Point: score, IsWalkOver: "N", RemainingHole: isPlayOff ? 0 : 18 - thru, IsFinal: "N" }
                : { ID: 0, MatchPlayPlayerVsID: activePlayerVs.ID, User1ID: leaderId === activePlayerVs.User1ID ? activePlayerVs.User1ID : activePlayerVs.User3ID, User2ID: leaderId === activePlayerVs.User1ID ? activePlayerVs.User2ID : activePlayerVs.User4ID, Point: score, IsWalkOver: "N", RemainingHole: isPlayOff ? 0 : 18 - thru, IsFinal: "N" };
            const winnerHolePayload = [{ ID: 0, MatchPlayPlayerVsID: activePlayerVs.ID, Hole: thru, User1ID: winnerPayload.User1ID, User2ID: winnerPayload?.User2ID || 0, Point: score, PlayOff: isPlayOff ? "Y" : "N" }];

            swal.fire({ titleText: "Saving", text: "Please Wait...", showConfirmButton: false, allowOutsideClick: false, onOpen: () => swal.showLoading() });

            // ↓ Swap for your actual API function
            const res = await postScoreCard({ matchPlayWinner: winnerPayload, matchPlayWinnerHole: winnerHolePayload });

            swal.close();
            if (res.Message === "success") {
                swal.fire({ icon: "success", titleText: "Winner submitted.", showConfirmButton: false, timer: 1500 });
                setShowScoreModal(false);
                await refreshWinners(matchPlay.ID);
            } else {
                swal.fire({ icon: "error", titleText: "Error!", text: res.LongMessage });
            }
        } catch (err) {
            swal.close();
            swal.fire({ icon: "error", titleText: err.message });
        }
    };

    const onVerifyScore = async () => {
        const confirm = await swal.fire({
            icon: "question",
            titleText: "Verify Score",
            text: "Are you sure you want to verify this score? This action cannot be undone.",
            showCancelButton: true,
            confirmButtonText: "Yes, Verify",
            cancelButtonText: "Cancel",
            buttonsStyling: true,
            reverseButtons: true,
        });

        if (!confirm.isConfirmed) return;

        try {
            swal.fire({
                titleText: "Verifying",
                text: "Please Wait...",
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                onOpen: () => swal.showLoading(),
            });

            const res = await verifyScore(activePlayerVs.ID);
            swal.close();
            if (res.Message === "success") {
                swal.fire({
                    icon: "success",
                    titleText: "Score Verified!",
                    text: "The score has been successfully verified.",
                    showConfirmButton: false,
                    timer: 1500,
                });
                await refreshWinners(matchPlay.ID);
            } else {
                swal.fire({
                    icon: "error",
                    titleText: "Verification Failed",
                    text: res.LongMessage || "Something went wrong. Please try again.",
                });
            }
        } catch (err) {
            swal.close();
            swal.fire({
                icon: "error",
                titleText: "Unexpected Error",
                text: err.message,
            });
        }
    };

    const emailWinner = async () => {
        const lastRound   = rounds[rounds.length - 1];
        if (!lastRound) return;
        const lastVs      = matchPlayPlayersVs.find(vs => vs.MatchPlayRoundID === lastRound.ID);
        if (!lastVs) return;
        const finalWinner = matchPlayWinners.find(w => w.MatchPlayPlayerVsID === lastVs.ID);
        if (!finalWinner) return;
        try {
            swal.fire({ titleText: "Sending Email", text: "Please Wait...", showConfirmButton: false, allowOutsideClick: false, onOpen: () => swal.showLoading() });

            const res = await emailMatchPlayWinner(finalWinner);
            // swal.close();
            if (res.Message === "success") {
                swal.fire({ icon: "success", titleText: "Email Sent Successfully.", showConfirmButton: false, timer: 1500 });
                setTimeout(() => window.location.reload(), 500);
            } else {
                swal.fire({ icon: "error", titleText: "Error!", text: res.LongMessage });
            }
        } catch (err) {
            swal.close();
            swal.fire({ icon: "error", titleText: err.message });
        }
    };

    const postWalkover = async (side, checked) => {
            if (!activePlayerVs) return;

            const winnerData = matchPlay.Type === "Doubles" ? (
                side === "LHS"
                    ? {
                        user1ID: activePlayerVs?.User1ID,
                        user2ID: activePlayerVs?.User2ID,
                        winnerName: `${activePlayerVs?.User1Name} | ${activePlayerVs?.User2Name}`
                    }
                    : {
                        user1ID: activePlayerVs?.User3ID,
                        user2ID: activePlayerVs?.User4ID,
                        winnerName: `${activePlayerVs?.User3Name} | ${activePlayerVs?.User4Name}`
                    }
                )
                : (side === "LHS"
                    ? {
                        user1ID: activePlayerVs?.User1ID,
                        user2ID: null,
                        winnerName: activePlayerVs?.User1Name
                    }
                    : {
                        user1ID: activePlayerVs?.User2ID,
                        user2ID: null,// activePlayerVs?.User3ID
                        winnerName: activePlayerVs?.User2Name // activePlayerVs?.User3Name
                    }
            );
        
            try {
                swal.fire({
                    title: 'Are you sure?',
                    text: checked ? "Do you want to give walkover to " + winnerData?.winnerName + "?" : "Do you want to withdraw walkover given to " + winnerData?.winnerName + "?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                    // reverseButtons: true,
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        swal.fire({ titleText: "Saving", text: "Please Wait...", showConfirmButton: false, allowOutsideClick: false, onOpen: () => swal.showLoading() });

                        const walkOverData = {
                            ID                  : (activeWinner?.ID) ? activeWinner.ID : 0,
                            MatchPlayPlayerVsID : activePlayerVs.ID,
                            User1ID             : checked ? winnerData.user1ID : 0,
                            User2ID             : checked ? winnerData.user2ID : 0,
                            Point               : 0,
                            IsWalkOver          : checked ? "Y" : "N",
                            RemainingHole       : 0
                        }
                        const res = await walkoverChangeAPI(walkOverData);

                        swal.close();
                        if (res.Message === "success") {
                            await refreshWinners(matchPlay.ID);
                        } else {
                            swal.fire({ icon: "error", titleText: "Error!", text: res.LongMessage });
                        }
                    }
                });
            } catch (err) {
                swal.close();
                swal.fire({ icon: "error", titleText: err.message });
            }
    };
    //#endregion

    //#region Handlers
    const onSelectHandler = async (master) => {
        if (!master) return;
        setMMaster(master);
        await fetchMatchPlayScorecard(master.ID);
    };

    const onReset = () => {
        setMMaster(null);
        setMatchPlay({});
        setRounds([]);
        setRegisteredPlayers([]);
        setMatchPlayPlayers([]);
        setMatchPlayPlayersVs([]);
        setMatchPlayWinners([]);
        setMatchPlayWinnerHoles([]);
        setSelectedRound({ round: 1, roundName: "Round 1" });
        setDrawerOpen(false);         // close drawer on reset
        setActivePlayerVs(null);
        setActiveWinner(null);
        setIsFinalWinnerDeclared(false);
    };

    const onMatchClick = (playerVs) => {
        if (!playerVs) return;
        const winner = matchPlayWinners.find(w => w.MatchPlayPlayerVsID === playerVs.ID);
        setActivePlayerVs(playerVs);
        setActiveWinner(winner || null);
        setDrawerOpen(true);         
    };

    const closeDrawer = () => {
        setDrawerOpen(false);      
    };

    const showEmailWinner = () => {
        const lastRound = rounds[rounds.length - 1];
        if (!lastRound) return false;
        const lastVs    = matchPlayPlayersVs.find(vs => vs.MatchPlayRoundID === lastRound.ID);
        if (!lastVs) return false;
        const finalWinner = matchPlayWinners.find(w => w.MatchPlayPlayerVsID === lastVs.ID);
        return finalWinner && lastVs.IsScoreVerified === "Y" && finalWinner.IsFinal === "Y";
    };

    const vsForRound = currentRoundVs();
    //#endregion

    return (
        <>
            <MatchPlayList
                title="Match Play Scorecard"
                comp="scorecard"
                onSelectHandler={onSelectHandler}
                resetData={onReset}
            >
                <div className="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor padding-b0">
                    <div className="kt-container--fluid kt-grid__item kt-grid__item--fluid">
                        <div className="kt-portlet kt-portlet--mobile padding-lr20">

                            {/* INFO PANEL */}
                            <div className="kt-portlet__head kt-portlet__head--lg d-block padding-lr0 border-0">
                                {!!matchPlay?.Name && (
                                    <div className="kt-notification margin-t10 mb-3" style={{ backgroundColor: "rgb(247,248,250)" }}>
                                        <div className="kt-notification__item d-block">
                                            <div className="row">
                                                <div className="col-8">
                                                    <div className="kt-user-card-v2 mb-2">
                                                        <div className="kt-user-card-v2__pic">
                                                            <img src={DEFAULT_USER_PHOTO} alt="matchplay-logo" style={{ height: 40 }} />
                                                        </div>
                                                        <div className="kt-user-card-v2__details">
                                                            <span className="kt-user-card-v2__name text-clip" title={matchPlay.Name}>
                                                                {matchPlay.Name?.length > 30 ? matchPlay.Name.substring(0, 30) + "..." : matchPlay.Name}
                                                            </span>
                                                            <span className="kt-user-card-v2__desc">
                                                                {matchPlay.Type} | {matchPlay.MembershipType}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-2 d-flex-center">
                                                    <div className="kt-user-card-v2 mb-2">
                                                        <div className="kt-user-card-v2__details">
                                                            <span className="kt-user-card-v2__name">Start</span>
                                                            <span className="kt-user-card-v2__desc">{moment(matchPlay.StartDate).format("DD-MMM-YYYY")}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-2 d-flex-center">
                                                    <div className="kt-user-card-v2 mb-2">
                                                        <div className="kt-user-card-v2__details">
                                                            <span className="kt-user-card-v2__name">End</span>
                                                            <span className="kt-user-card-v2__desc">{moment(matchPlay.EndDate).format("DD-MMM-YYYY")}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>


                        </div>

                        {/* ROUND TABS */}
                        {rounds.length > 0 && (
                            <>
                                <div className={`kt-wizard-v4 wizard-r${rounds.length}`} id="kt_user_add_user">
                                    <div className="kt-wizard-v4__nav">
                                        <div className="kt-wizard-v4__nav-items nav">
                                            {rounds.map((round) => (
                                                <div
                                                    key={round.ID}
                                                    className={`pnl-round-tab kt-wizard-v4__nav-item nav-item${selectedRound.round === round.Round ? " bg-white" : ""}`}
                                                    onClick={() => setSelectedRound({ round: round.Round, roundName: round.roundName })}
                                                >
                                                    <div className="kt-wizard-v4__nav-body">
                                                        <div className={`kt-wizard-v4__nav-number${selectedRound.round === round.Round ? " text-white bg-info" : " text-info"}`}>
                                                            {round.Round}
                                                        </div>
                                                        <div className="kt-wizard-v4__nav-label">
                                                            <div className={`kt-wizard-v4__nav-label-title${selectedRound.round === round.Round ? " text-info" : ""}`}>
                                                                {round.roundName}
                                                            </div>
                                                            <div className="kt-wizard-v4__nav-label-desc">
                                                                {moment(round.EndDate).format("DD MMM, YYYY")}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* ROUND CONTENT */}
                                <div className="kt-portlet kt-portlet--mobile tab-content margin-t10">
                                    <div className="pnl-round" id={`round${selectedRound.round}`}>
                                        <div className="kt-portlet__head">
                                            <div className="kt-portlet__head-label">
                                                <h3 className="kt-portlet__head-title">{selectedRound.roundName}</h3>
                                            </div>
                                            <div className="kt-widget__button margin-t15 d-flex align-items-center gap-2">
                                                {showEmailWinner() && (
                                                    <button type="button" className="btn btn-brand btn-sm d-inline-block margin-r10" onClick={emailWinner}>
                                                        <i className="fa fa-envelope" /> Email Winner
                                                    </button>
                                                )}
                                                <span className="btn btn-label-brand btn-sm">
                                                    Total {matchPlay?.Type === "Singles" ? "Player" : "Team"} :{" "}
                                                    <b>{selectedRound?.round === 1 ? matchPlayPlayers.length : vsForRound.length * 2}</b>
                                                </span>
                                            </div>
                                        </div>

                                        <div className="kt-portlet__body">
                                            {vsForRound.length === 0 ? (
                                                <div className="text-center padding-20 text-muted">
                                                    No matches for this round yet.
                                                </div>
                                            ) : (
                                                vsForRound.map((playerVs, idx) => (
                                                    <MatchCard
                                                        key={playerVs.ID}
                                                        matchIndex={idx + 1}
                                                        groupIndex={Math.floor(idx / 2) + 1}
                                                        playerVs={playerVs}
                                                        winner={getWinnersForVs(playerVs.ID)}
                                                        winnerHoles={getWinnerHolesForVs(playerVs.ID)}
                                                        registeredPlayers={registeredPlayers}
                                                        type={matchPlay?.Type || "Singles"}
                                                        userPhotoURL={userPhotoURL}
                                                        onMatchClick={onMatchClick}
                                                    />
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </MatchPlayList>

            {/* Scorecard Modal (player info + walkover + action buttons) */}
            <ScorecardModal
                open={drawerOpen}
                onClose={closeDrawer}
                playerVs={activePlayerVs}
                winner={activeWinner}
                type={matchPlay?.Type}
                onUpdateScore={() => setShowScoreModal(true)}
                onVerifyScore={onVerifyScore}
                onWalkoverChange={postWalkover}
            />

            {/* Score Modal */}
            <ScoreModal
                show={showScoreModal}
                onClose={() => setShowScoreModal(false)}
                onSave={postScore}
                playerVs={activePlayerVs}
                winner={activeWinner}
                winnerHoles={getActiveWinnerHoles()}
                type={matchPlay?.Type || "Singles"}
            />
        </>
    );
};

export default MatchPlayScorecard;